import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CardFormDialog } from "components/main/organizations/CardFormDialog";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useShallow } from "zustand/react/shallow";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { TextInput } from "components/common/TextInput";
import { SectionRule } from "components/common/SectionRule";
import { StatusChip } from "components/common/StatusChip";
import { Stack } from "components/common/Stack";
import { useMySpend } from "@/lib/api/apps/hooks";
import { spendLabel, usdMicros } from "@/components/main/apps/app-format";
import { formatShortDate } from "@/lib/dates";
import { Tooltip } from "components/common/Tooltip";
import { CopyButton } from "components/common/CopyButton";
import { Callout } from "components/common/Callout";
import {
  IconCount,
  ImpactChips,
  ImpactRow,
  plural,
} from "components/common/ImpactRow";
import { CriticalTableRow } from "components/main/utils/settings/account-deletion/CriticalTableRow";
import type { TableWithModel } from "components/main/utils/settings/account-deletion/useDeleteAccount";
import { MoveTableDialog } from "components/canvas/detailsSide/utils/ownership/MoveTableDialog";
import { mcpServerUrl } from "@/components/main/utils/tokens/mcpConnect";
import { Badge } from "components/common/Badge";
import { ImageRow } from "components/common/ImageRow";
import { EditableName } from "components/canvas/utils/EditableName";
import { OrgMark } from "./OrgMark";
import { DangerLink } from "@/components/main/utils/settings/SettingsFrame";
import { OrgList } from "@/components/main/utils/settings/OrgList";
import { useAccountSettingsStore } from "stores/useAccountSettingsStore";
import { TooltipButton } from "components/common/TooltipButton";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  cancelOrgDeletion,
  deleteOrg,
  fetchLeaveOrgPreview,
  isEnded,
  mintCardTransaction,
  SUBSCRIPTION_ENDED,
  getOrgDeletionAnalysis,
  leaveOrg,
  removeOrgImage,
  renameOrg,
  updateOrgImage,
  type LeaveOrgPreview,
  type Org,
  type OrgDeletionAnalysis,
  type OrgMember,
} from "@/lib/api/organizations";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import { formatDayMonthYear } from "@/lib/dates";
import { GRACE_DAYS, purgeDate } from "@/lib/deletionGrace";
import { message } from "@/lib/serverError";

export const GeneralPanel: React.FC<{
  org: Org;
  members: OrgMember[];
}> = ({ org, members }) => {
  const navigate = useNavigate();
  const orgSpaces = useOrgContextStore((s) => s.spaces);
  const { refreshOrgs, refreshSpaces, switchOrg, activeOrgId } =
    useOrgContextStore(
      useShallow((s) => ({
        refreshOrgs: s.refreshOrgs,
        refreshSpaces: s.refreshSpaces,
        switchOrg: s.switchOrg,
        activeOrgId: s.activeOrgId,
      })),
    );

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leavePreview, setLeavePreview] = useState<LeaveOrgPreview | null>(
    null,
  );
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [analysis, setAnalysis] = useState<OrgDeletionAnalysis | null>(null);
  const [isCancellingDeletion, setIsCancellingDeletion] = useState(false);

  // The org-scoped MCP alias: connections through it are confined to this org.
  const mcpEndpointUrl = mcpServerUrl(org.org_id);
  const isOwner = org.is_personal || org.role === "owner";
  const isGovernor = org.role === "owner" || org.role === "org_admin";

  // The pre-flight is what turns "delete this org" into an informed decision,
  // so the dialog opens on its result rather than racing it.
  const [movingTable, setMovingTable] = useState<TableWithModel | null>(null);

  const openDeleteDialog = async () => {
    setAnalysis(null);
    setDeleteConfirmName("");
    setDeleteOpen(true);
    try {
      setAnalysis(await getOrgDeletionAnalysis(org.org_id));
    } catch {
      // Leave `analysis` null — the dialog falls back to generic wording
      // rather than blocking the door on a failed count.
    }
  };
  // `critical_tables` is only a count; the identities are already in the
  // per-model breakdown, which is what the rescue needs.
  const criticalTables = useMemo<TableWithModel[]>(
    () =>
      (analysis?.models ?? []).flatMap((m) =>
        m.tables
          .filter((t) => t.usage_status === "Critical")
          .map((t) => ({
            ...t,
            model_id: m.model_id,
            model_name: m.model_name,
          })),
      ),
    [analysis],
  );

  const owners = members.filter((m) => m.role === "owner");

  const openLeave = async () => {
    setLeavePreview(null);
    setLeaveOpen(true);
    try {
      setLeavePreview(await fetchLeaveOrgPreview(org.org_id));
    } catch (error) {
      setLeaveOpen(false);
      showNotificationGlobal(message(error, "Could not check"), "error");
    }
  };

  /// The leaver is still an owner here, so the card is still theirs to replace.
  const openCardForm = async () => {
    const transaction = await mintCardTransaction(org.org_id);
    if (isEnded(transaction)) {
      showNotificationGlobal(SUBSCRIPTION_ENDED);
      return;
    }
    if (!transaction) {
      showNotificationGlobal("The card form could not be opened.", "error");
      return;
    }
    setCardTransaction(transaction);
  };

  const [cardTransaction, setCardTransaction] = useState<string | null>(null);

  const confirmLeave = async () => {
    try {
      await leaveOrg(org.org_id);
      showNotificationGlobal(`You left ${org.name}`, "success");
      setLeaveOpen(false);
      await refreshOrgs();
      if (activeOrgId === org.org_id) {
        const fallback = useOrgContextStore
          .getState()
          .orgs.find((o) => o.is_personal);
        if (fallback) await switchOrg(fallback.org_id);
      }
      navigate("/overview");
    } catch (error) {
      showNotificationGlobal(message(error, "Could not leave"), "error");
    }
  };

  const confirmDelete = async () => {
    try {
      const { purge_at } = await deleteOrg(org.org_id);
      showNotificationGlobal(
        `"${org.name}" will be deleted on ${formatDayMonthYear(purge_at)}. Any admin can cancel until then.`,
        "success",
      );
      setDeleteOpen(false);
      setDeleteConfirmName("");
      // The schedule fans out to every space in the org, so the rail is stale
      // until it re-reads — same on the way back in `undoDeletion`.
      await Promise.all([refreshOrgs(), refreshSpaces()]);
    } catch (error) {
      showNotificationGlobal(message(error, "Delete failed"), "error");
    }
  };

  const undoDeletion = async () => {
    setIsCancellingDeletion(true);
    try {
      await cancelOrgDeletion(org.org_id);
      showNotificationGlobal(`Deletion of "${org.name}" cancelled`, "success");
      await Promise.all([refreshOrgs(), refreshSpaces()]);
    } catch (error) {
      showNotificationGlobal(
        message(error, "Could not cancel the deletion"),
        "error",
      );
    } finally {
      setIsCancellingDeletion(false);
    }
  };

  const closesOn = org.deletion_scheduled_at
    ? org.purge_at
      ? formatDayMonthYear(org.purge_at)
      : purgeDate(org.deletion_scheduled_at)
    : "";

  return (
    <>
      <section className="flex flex-col">
        <SectionRule label="Identity" />
        <OrgIdentity key={org.org_id} org={org} />
        <div className="flex min-h-8 items-center gap-2 px-2">
          <span className="w-33 shrink-0 text-fg-muted-medium">
            Organization ID
          </span>
          <span
            className="min-w-0 break-all select-text font-mono text-fg-muted"
            style={{ fontSize: "var(--font-size-small)" }}
          >
            {org.org_id}
          </span>
          <CopyButton text={org.org_id} ariaLabel="Copy organization ID" />
        </div>
        <div className="flex min-h-8 items-center gap-2 px-2">
          <span className="w-33 shrink-0 text-fg-muted-medium">
            MCP endpoint
          </span>
          <Tooltip title="Connections made with this URL are confined to this organization's data">
            <span
              className="truncate select-text font-mono text-fg-muted"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {mcpEndpointUrl}
            </span>
          </Tooltip>
          <CopyButton text={mcpEndpointUrl} ariaLabel="Copy MCP endpoint" />
        </div>
        <div className="flex min-h-8 items-center gap-2 px-2">
          <span className="w-33 shrink-0 text-fg-muted-medium">Created</span>
          <span className="text-fg-regular">
            {formatDayMonthYear(org.created_at)}
          </span>
        </div>
        {!org.is_personal && owners.length > 0 && (
          <div className="flex min-h-8 gap-2 px-2 py-1">
            <span className="w-33 shrink-0 pt-0.5 text-fg-muted-medium">
              {owners.length > 1 ? "Owners" : "Owner"}
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              {owners.map((o) => (
                <span key={o.client_id} className="truncate text-fg-regular">
                  {o.display_name || o.email}
                  {o.email && o.display_name && (
                    <span className="pl-2 text-fg-muted-regular-small">
                      {o.email}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
        {!org.is_personal && owners.length === 1 && (
          <div className="flex gap-2 px-2 pb-1">
            <span className="w-33 shrink-0" />
            <span className="text-fg-muted-regular-small">
              A second owner keeps the organization reachable if this one is not
              — and is required before that owner can close their account.
            </span>
          </div>
        )}
      </section>

      {/* The org every account has lists the others and makes new ones, as the account card does. */}
      {org.is_personal && (
        <OrgList
          onOpenOrg={(other) => {
            void switchOrg(other.org_id);
            navigate(`/organizations/${other.org_id}`);
          }}
          onCreateOrg={() => navigate("/organizations/new")}
        />
      )}

      {!org.is_personal && <MySpendSection orgId={org.org_id} />}

      {/* Each destructive act is a box that warms to red, so it needs no Danger rule. */}
      <section aria-label="Danger" className="flex flex-col gap-2">
        {org.is_personal ? (
          // The act lives on the account card, opened straight on it.
          <DangerLink
            label={
              org.deletion_scheduled_at
                ? "Account deletion scheduled"
                : "Delete account"
            }
            detail={
              org.deletion_scheduled_at
                ? org.deletion_started_at
                  ? "The teardown is running now and can no longer be stopped."
                  : `Goes with your account on ${closesOn}. Cancel the account deletion and all of it comes back.`
                : undefined
            }
            onClick={() =>
              useAccountSettingsStore
                .getState()
                .show(org.deletion_scheduled_at ? "home" : "delete")
            }
          />
        ) : org.deletion_scheduled_at ? (
          <Callout
            variant="warning"
            title={
              org.deletion_started_at
                ? "Being deleted"
                : `Scheduled for deletion on ${closesOn}`
            }
          >
            <span className="flex flex-col items-start gap-2">
              {org.deletion_started_at
                ? "The teardown is running now. Everything in this organization is on its way out, and it can no longer be stopped."
                : "Every space and model in it is in the Trash until then, and nothing new can be added. Cancelling brings all of it back."}
              {!org.deletion_started_at && (
                <TooltipButton
                  title={isGovernor ? "" : "Only an owner or admin can cancel."}
                  variant="secondary"
                  size="sm"
                  loading={isCancellingDeletion}
                  disabled={!isGovernor || isCancellingDeletion}
                  onClick={() => void undoDeletion()}
                >
                  Cancel deletion
                </TooltipButton>
              )}
            </span>
          </Callout>
        ) : (
          <>
            {/* Offered to an owner too: co-ownership (D46) means most owners
                CAN leave, and the last one is told why they cannot inside the
                dialog rather than by a dead button. */}
            <DangerLink
              label="Leave organization"
              onClick={() => void openLeave()}
            />
            <DangerLink
              label="Delete organization"
              refusal={
                isOwner
                  ? undefined
                  : "Only an owner can delete the organization."
              }
              onClick={() => void openDeleteDialog()}
            />
          </>
        )}
      </section>

      <Dialog
        open={deleteOpen}
        title={`Delete "${org.name}"?`}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteConfirmName("");
        }}
      >
        <Stack gap="md">
          <span className="text-fg-regular">
            Goes to the Trash for {GRACE_DAYS} days, where any owner or admin
            can restore it and everything in it. Nothing new can be added
            meanwhile.
          </span>
          {analysis && (
            <>
              <ImpactRow
                quiet
                icon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
                count={analysis.spaces.length}
                label={plural(analysis.spaces.length, "space")}
              />
              <ImpactRow
                quiet
                icon={<DashboardOutlinedIcon sx={{ fontSize: 16 }} />}
                count={analysis.models.length}
                label={plural(analysis.models.length, "model")}
              />
              <ImpactRow
                quiet
                icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                count={analysis.total_tables}
                label={plural(analysis.total_tables, "table")}
              />
              <ImpactRow
                quiet
                icon={<PeopleOutlinedIcon sx={{ fontSize: 16 }} />}
                count={analysis.member_count}
                label={`${plural(analysis.member_count, "member")} removed`}
              >
                {analysis.contributors.length > 0 && (
                  <ImpactChips
                    items={analysis.contributors.map((c) => ({
                      key: c.client_id,
                      label: (
                        <>
                          {c.display_name}
                          <IconCount
                            count={c.table_count}
                            icon={
                              <TableChartOutlinedIcon className="icon-xs" />
                            }
                            label={`${c.table_count} ${plural(c.table_count, "table")}`}
                          />
                        </>
                      ),
                    }))}
                  />
                )}
              </ImpactRow>
              {analysis.critical_tables > 0 && (
                <ImpactRow
                  quiet
                  tone="warning"
                  icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />}
                  count={analysis.critical_tables}
                  label={`${plural(analysis.critical_tables, "table")} imported by other organizations`}
                  hint={
                    criticalTables.length > 0
                      ? "Their importers lose the data unless you move the table into a model you keep."
                      : "Their importers lose the data when the window closes."
                  }
                >
                  {criticalTables.length > 0 && (
                    <div className="flex max-h-40 flex-col overflow-y-auto">
                      {criticalTables.map((t) => (
                        <CriticalTableRow
                          key={t.table_id}
                          table={t}
                          onMove={() => setMovingTable(t)}
                        />
                      ))}
                    </div>
                  )}
                </ImpactRow>
              )}
            </>
          )}
        </Stack>
        <span className="block text-fg-regular">
          Type <span className="font-medium">{org.name}</span> to confirm.
        </span>
        <TextInput
          value={deleteConfirmName}
          onChange={setDeleteConfirmName}
          ariaLabel="Type the organization name to confirm deletion"
          autoFocus
        />
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => {
                setDeleteOpen(false);
                setDeleteConfirmName("");
              },
            },
            {
              label: "Schedule deletion",
              variant: "primary",
              disabled: deleteConfirmName.trim() !== org.name,
              onClick: () => void confirmDelete(),
            },
          ]}
        />
      </Dialog>

      {movingTable && (
        <MoveTableDialog
          tableId={movingTable.table_id}
          tableName={movingTable.table_name}
          open
          onOpenChange={(next) => {
            if (next) return;
            setMovingTable(null);
            // A moved table leaves the org, so the counts above are now stale.
            void getOrgDeletionAnalysis(org.org_id)
              .then(setAnalysis)
              .catch(() => {});
          }}
        />
      )}

      <Dialog
        open={leaveOpen}
        title={`Leave "${org.name}"?`}
        onClose={() => setLeaveOpen(false)}
      >
        {leavePreview === null ? (
          <span className="text-fg-muted-regular">
            Checking the effects of leaving…
          </span>
        ) : (
          <div className="flex flex-col gap-2">
            {leavePreview.is_owner_blocked && (
              <Callout variant="warning" title="You are the only owner">
                Make someone else an owner in Members first, or delete the
                organization.
              </Callout>
            )}
            {leavePreview.pays_for_org && (
              <Callout
                variant="warning"
                title="The subscription is on your card"
              >
                <span className="flex flex-col items-start gap-2">
                  <span>It keeps charging you after you leave.</span>
                  <TooltipButton
                    title="Replace the card this subscription is charged on. Opens here."
                    variant="secondary"
                    size="sm"
                    onClick={() => void openCardForm()}
                    data-testid="leave-update-card"
                  >
                    Update card
                  </TooltipButton>
                </span>
              </Callout>
            )}
            {leavePreview.spaces_to_orphan.length > 0 && (
              <Callout variant="warning" title="You are the only manager">
                <span className="flex flex-col items-start">
                  Make someone else a manager before you leave, or an admin
                  appoints one after.
                  <ImpactChips
                    items={leavePreview.spaces_to_orphan.map((name) => {
                      // The preview names them; the dot comes from the org's spaces.
                      const space = orgSpaces.find((sp) => sp.name === name);
                      return {
                        key: name,
                        label: (
                          <>
                            <SpaceDot
                              color={spaceColor(
                                space ?? { space_id: name, color: "" },
                              )}
                            />
                            {name}
                          </>
                        ),
                      };
                    })}
                  />
                </span>
              </Callout>
            )}
            {leaveBlocked(leavePreview) ? null : (
              <>
                <span className="text-fg-regular">
                  You will lose access to all content in this organization.
                </span>
                {leavePreview.spaces_to_delete.length > 0 && (
                  <span className="text-fg-regular">
                    Your empty{" "}
                    {leavePreview.spaces_to_delete.length === 1
                      ? "space"
                      : "spaces"}{" "}
                    <span className="text-fg-medium">
                      {leavePreview.spaces_to_delete.join(", ")}
                    </span>{" "}
                    will be deleted.
                  </span>
                )}
                {leavePreview.grants_to_revoke.length > 0 && (
                  <span className="text-fg-regular">
                    Your access to{" "}
                    <span className="text-fg-medium">
                      {leavePreview.grants_to_revoke.join(", ")}
                    </span>{" "}
                    will be revoked; other members keep theirs.
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setLeaveOpen(false),
            },
            {
              label: "Leave organization",
              variant: "primary",
              disabled: leavePreview === null || leaveBlocked(leavePreview),
              onClick: () => void confirmLeave(),
            },
          ]}
        />
      </Dialog>
      <CardFormDialog
        transactionId={cardTransaction}
        onClose={() => setCardTransaction(null)}
        onCompleted={() => setCardTransaction(null)}
        onSettled={() => undefined}
      />
    </>
  );
};

/// An ownerless org is unrecoverable, so that still refuses. A stranded space
/// does not: a governor can reassign it (D49).
const leaveBlocked = (preview: LeaveOrgPreview) => preview.is_owner_blocked;

/** What the caller spent on the organization's own model keys this period,
 *  against their limit. Only once there is something to say. */
const MySpendSection: React.FC<{ orgId: string }> = ({ orgId }) => {
  const mine = useMySpend(orgId);
  if (!mine.data) return null;
  const { spent_micros, limit_cents, providers, resets_at } = mine.data;
  if (spent_micros === 0 && limit_cents == null && providers.length === 0)
    return null;
  const spend = spendLabel(spent_micros, limit_cents);
  return (
    <section className="flex flex-col">
      <SectionRule label="You" />
      <div className="flex items-center gap-3 px-2 py-2">
        <span className="text-fg">Spend on the organization's keys</span>
        <StatusChip tone={spend.near ? "warning" : "muted"}>
          {spend.text}
        </StatusChip>
        {providers.map((p) => (
          <StatusChip key={p.id} tone="muted">
            {p.name} {usdMicros(p.spent_micros)}
          </StatusChip>
        ))}
        <span className="ml-auto text-fg-muted-regular-small">
          resets {formatShortDate(Date.parse(resets_at))}
        </span>
      </div>
    </section>
  );
};

/// The org's mark, name and kind, edited in place.
const OrgIdentity: React.FC<{ org: Org }> = ({ org }) => {
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);
  const [isEditingName, setIsEditingName] = useState(false);
  const canGovern =
    org.is_personal || org.role === "owner" || org.role === "org_admin";

  // `EditableName.commit` runs synchronously and reads the return value, so
  // the PUT is fired off and its result handled later.
  const commitName = (name: string): boolean => {
    const trimmed = name.trim();
    if (trimmed === org.name) return true;
    if (trimmed.length < 2) {
      showNotificationGlobal(
        "Organization name must be at least 2 characters",
        "error",
      );
      return false;
    }
    void renameOrg(org.org_id, trimmed)
      .then(() => refreshOrgs())
      .catch((error: unknown) =>
        showNotificationGlobal(message(error, "Rename failed"), "error"),
      );
    return true;
  };

  // `has_image` first, so no OrgMark asks for a removed image.
  const showImageChange = async () => {
    await refreshOrgs();
    useOrgContextStore.getState().bumpImageVersion();
  };

  return (
    <ImageRow
      className="flex items-center gap-4 px-2 py-2"
      picture={<OrgMark org={org} size="lg" />}
      hasImage={org.has_image}
      noun="image"
      label="Change organization image"
      canChange={canGovern}
      onUpload={(image) =>
        updateOrgImage(org.org_id, image).then(showImageChange)
      }
      onRemove={() => removeOrgImage(org.org_id).then(showImageChange)}
    >
      <span className="flex min-h-8 items-center gap-2">
        <span className="min-w-0 text-fg-title">
          <EditableName
            name={org.name}
            isEditing={isEditingName}
            canEdit={canGovern}
            editTrigger="icon"
            ariaLabel="Organization name"
            setIsEditing={setIsEditingName}
            update={() => {}}
            commit={commitName}
            abort={() => setIsEditingName(false)}
          />
        </span>
        <Badge
          label={org.is_personal ? "Personal" : "Organization"}
          className="shrink-0"
        />
      </span>
    </ImageRow>
  );
};
